import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Button, ErrorNote, Field, Loading, Modal, inputClass } from '../../components/ui';
import { errorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth-context-value';
import { initials } from '../../lib/format';
import { profile as profileApi } from '../../lib/resources';
import type { Profile } from '../../types';
import { TimezoneSelect } from './CompanySection';

/** Configurações › Meu perfil — dados do usuário, não da empresa. */
export function ProfileSection() {
  const query = useQuery({ queryKey: ['profile'], queryFn: profileApi.get });
  if (query.isLoading || !query.data) return <Loading />;
  return <ProfileForm key={query.data.id} profile={query.data} />;
}

function ProfileForm({ profile }: { profile: Profile }) {
  const queryClient = useQueryClient();
  const { logout, refreshSession } = useAuth();
  const [form, setForm] = useState({
    name: profile.name,
    phone: profile.phone ?? '',
    timezone: profile.timezone,
  });
  const [saved, setSaved] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      profileApi.update({
        name: form.name,
        phone: form.phone || null,
        timezone: form.timezone,
      }),
    onMutate: () => setSaved(false),
    onSuccess: async (next) => {
      queryClient.setQueryData(['profile'], next);
      setSaved(true);
      await refreshSession();
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-900 text-lg font-semibold text-white">
            {initials(profile.name)}
          </div>
          <div>
            <p className="text-base font-semibold text-slate-900">{profile.name}</p>
            <p className="text-xs text-slate-500">
              Cadastro em {new Date(profile.createdAt).toLocaleDateString('pt-BR')}
            </p>
          </div>
        </div>
        <Button variant="ghost" onClick={logout}>
          Sair
        </Button>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <form onSubmit={submit}>
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Nome *">
              <input
                required
                minLength={2}
                className={inputClass}
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
            </Field>
            <Field label="Telefone">
              <input
                className={inputClass}
                placeholder="+55 85 99999-0000"
                value={form.phone}
                onChange={(event) => setForm({ ...form, phone: event.target.value })}
              />
            </Field>
            <Field label="Fuso horário" hint="Separado do fuso da empresa.">
              <TimezoneSelect
                value={form.timezone}
                onChange={(timezone) => setForm({ ...form, timezone })}
              />
            </Field>
            <Field label="E-mail" hint="É a identidade de login e não muda aqui.">
              <input disabled className={inputClass} value={profile.email} />
            </Field>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button type="submit" variant="primary" disabled={save.isPending}>
              {save.isPending ? 'Salvando…' : 'Salvar'}
            </Button>
            <Button onClick={() => setChangingPassword(true)}>Alterar senha</Button>
            {saved && <span className="text-xs text-emerald-600">Salvo.</span>}
            {save.error && (
              <ErrorNote>{errorMessage(save.error, 'Não foi possível salvar')}</ErrorNote>
            )}
          </div>
        </form>
      </section>

      {changingPassword && <PasswordModal onClose={() => setChangingPassword(false)} />}
    </div>
  );
}

function PasswordModal({ onClose }: { onClose: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [done, setDone] = useState(false);
  const change = useMutation({
    mutationFn: () => profileApi.changePassword(current, next),
    onSuccess: () => setDone(true),
  });
  const mismatch = repeat.length > 0 && repeat !== next;

  return (
    <Modal title="Alterar senha" onClose={onClose}>
      {done ? (
        <div className="space-y-4 text-sm text-slate-600">
          <p>Senha alterada. Use a nova senha no próximo login.</p>
          <div className="flex justify-end">
            <Button variant="primary" onClick={onClose}>
              Fechar
            </Button>
          </div>
        </div>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!mismatch) change.mutate();
          }}
        >
          <Field label="Senha atual">
            <input
              required
              type="password"
              autoComplete="current-password"
              className={inputClass}
              value={current}
              onChange={(event) => setCurrent(event.target.value)}
            />
          </Field>
          <Field label="Nova senha" hint="Mínimo de 8 caracteres.">
            <input
              required
              minLength={8}
              type="password"
              autoComplete="new-password"
              className={inputClass}
              value={next}
              onChange={(event) => setNext(event.target.value)}
            />
          </Field>
          <Field label="Repita a nova senha">
            <input
              required
              type="password"
              autoComplete="new-password"
              className={inputClass}
              value={repeat}
              onChange={(event) => setRepeat(event.target.value)}
            />
          </Field>
          {mismatch && <ErrorNote>As senhas não conferem.</ErrorNote>}
          {change.error && (
            <ErrorNote>{errorMessage(change.error, 'Não foi possível alterar')}</ErrorNote>
          )}
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>Cancelar</Button>
            <Button type="submit" variant="primary" disabled={change.isPending || mismatch}>
              {change.isPending ? 'Salvando…' : 'Alterar senha'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
